import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import toast from 'react-hot-toast';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit,
      });
      if (search) params.append('search', search);
      if (tier) params.append('tier', tier);

      const response = await client.get(`/api/v1/admin/users?${params}`);
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, search, tier]);

  useEffect(() => {
    fetchUsers();
  }, [page, search, tier, fetchUsers]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;

    try {
      await client.delete(`/api/v1/admin/users/${userId}`);
      toast.success('Kullanıcı başarıyla silindi');
      fetchUsers();
    } catch (error) {
      toast.error('Kullanıcı silinemedi');
    }
  };

  const handleTogglePremium = async (userId, currentTier) => {
    const newTier = currentTier === 'premium' ? 'free' : 'premium';
    try {
      await client.patch(`/api/v1/admin/users/${userId}/subscription?tier=${newTier}`);
      toast.success(`Abonelik ${newTier} olarak güncellendi`);
      fetchUsers();
    } catch (error) {
      toast.error('Abonelik güncellenemedi');
    }
  };

  const maxPages = Math.ceil(total / limit);
  const showingFrom = total > 0 ? page * limit + 1 : 0;
  const showingTo = Math.min((page + 1) * limit, total);

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">👥 Kullanıcılar</h2>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg p-4 mb-6 border border-slate-700 flex gap-4">
        <input
          type="text"
          placeholder="E-posta veya isim ara..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500"
        />
        <select
          value={tier}
          onChange={(e) => {
            setTier(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white"
        >
          <option value="">Tüm Abonelikler</option>
          <option value="free">Ücretsiz</option>
          <option value="premium">Premium</option>
          <option value="premium_plus">Premium Plus</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">E-posta</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">İsim</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">Burç</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">Abonelik</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">Kredi</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">Dil</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-200">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                    Yükleniyor...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                    Kullanıcı bulunamadı
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-t border-slate-700 hover:bg-slate-750 text-slate-300 cursor-pointer"
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <td className="px-6 py-3">{user.email}</td>
                    <td className="px-6 py-3">{user.full_name || '-'}</td>
                    <td className="px-6 py-3">{user.sun_sign || '-'}</td>
                    <td className="px-6 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          user.subscription_tier === 'premium'
                            ? 'bg-indigo-600 text-white'
                            : user.subscription_tier === 'premium_plus'
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-600 text-slate-300'
                        }`}
                      >
                        {user.subscription_tier}
                      </span>
                    </td>
                    <td className="px-6 py-3">{user.credits}</td>
                    <td className="px-6 py-3">
                      <span className="text-xs text-slate-400">{user.language || '—'}</span>
                    </td>
                    <td className="px-6 py-3 space-x-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleTogglePremium(user.id, user.subscription_tier)}
                        className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                      >
                        Premium Değiştir
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                      >
                        Sil
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
            <div className="text-slate-400 text-sm">
              {showingFrom}–{showingTo} / {total} gösteriliyor
            </div>
            <div className="space-x-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50 text-sm"
              >
                ← Önceki
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= maxPages - 1}
                className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50 text-sm"
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
